import { IconFilterList } from "@/assets/icons";
import { IconSort } from "@/assets/icons/IconSort";
import { SORTING } from "@/constants/sorting";
import { useSorting } from "@/hooks/useSorting";
import { FCC } from "@/types/common";
import { createUrlWithParams, getQueryFromURL } from "@/utils/url";
import { Stack } from "@mui/material";
import { Key, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type TTableSortingProps = {
  name: string;
  disabledValues?: Key[];
};

export const TableSorting: FCC<TTableSortingProps> = ({ name, children, disabledValues }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sorts } = useSorting();

  const onChange = (v: Key[]) => {
    const query = getQueryFromURL();
    let newSort = v[0].toString();

    if (sorts[0] === newSort) {
      newSort = "";
    }

    const url = createUrlWithParams(location.pathname, { ...query, sorts: newSort || undefined });
    navigate(url, { state: location.state });
  };

  const currentSelected = useMemo(() => {
    const found = sorts.find((sort) => sort.startsWith(name));
    if (!found) return undefined;
    return [found];
  }, [sorts]);

  const [selected, setSelected] = useState<Key[]>(currentSelected ?? []);

  useEffect(() => {
    setSelected(currentSelected ?? []);
  }, [currentSelected]);

  const shouldRotateIcon = selected.some((item) => typeof item === "string" && item.includes(SORTING.ASC));

  const handleSort = () => {
    let value = "";

    const hasAsc = selected.some((item) => typeof item === "string" && item.includes(SORTING.ASC));
    const hasDesc = selected.some((item) => typeof item === "string" && item.includes(SORTING.DESC));

    if (hasAsc && !disabledValues?.includes(SORTING.ASC)) {
      value = `${name}|${SORTING.DESC}`;
    } else if (hasDesc && !disabledValues?.includes(SORTING.DESC)) {
      value = "";
    } else {
      value = `${name}|${SORTING.ASC}`;
    }
    setSelected([value]);
    onChange?.([value]);
  };

  return (
    <Stack
      direction="row"
      gap={1.25}
      onClick={handleSort}
      sx={{
        cursor: "pointer",
      }}
    >
      {children}
      {selected.length > 0 ? (
        <IconSort style={shouldRotateIcon ? { transform: "rotate(180deg)" } : undefined} fontSize={24} />
      ) : (
        <IconFilterList fontSize={24} />
      )}
    </Stack>
  );
};
